import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircleHeart, Send, Check, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SectionLayout from "@/pages/SectionLayout";
import Lightbox from "@/components/Lightbox";
import TALES from "@/data/art";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Send cookies so the like-toggle device id is sticky across visits.
const http = axios.create({ baseURL: API, withCredentials: true });

const PIECES = TALES;

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ============================================================================
// EmailPromptModal — shown before a feeling is sent. Captures the visitor's
// email so the artist can follow up. Only on submit do we POST the comment.
// ============================================================================
const EmailPromptModal = ({ piece, draft, onClose, onSent }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RX.test(trimmed) || submitting) return;
    setSubmitting(true);
    try {
      await http.post("/comments", {
        image_id: piece.id,
        image_title: piece.title,
        text: draft,
        commenter_email: trimmed,
      });
      toast.success("Your feeling was sent.");
      onSent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="email-modal__backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="email-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="email-modal" onSubmit={submit}>
        <button
          type="button"
          className="email-modal__close"
          onClick={onClose}
          aria-label="Close"
          data-testid="email-modal-close"
        >
          <X size={16} strokeWidth={1.6} />
        </button>
        <p className="email-modal__eyebrow">One last step</p>
        <h3 className="email-modal__title">Leave a way back to you.</h3>
        <p className="email-modal__body">
          Your email stays private. The artist may reply — never marketed,
          never shared.
        </p>
        <input
          type="email"
          className="email-modal__input"
          placeholder="you@somewhere.com"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="email-modal-input"
        />
        <div className="email-modal__row">
          <button
            type="button"
            className="email-modal__btn email-modal__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="email-modal__btn"
            disabled={!EMAIL_RX.test(email.trim()) || submitting}
            data-testid="email-modal-submit"
          >
            <Send size={14} />
            <span>{submitting ? "Sending…" : "Send Feeling"}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// FeedCard — title, description slot, image, like (public count), feelings.
// ============================================================================
const FeedCard = ({ piece, count, liked, isMostLoved, onToggleLike, onOpen }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);

  const startSend = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setEmailModalOpen(true);
  };

  const handleSent = () => {
    setEmailModalOpen(false);
    setOpen(false);
    setText("");
    setSentOnce(true);
    setTimeout(() => setSentOnce(false), 2400);
  };

  return (
    <article className="feed-card" data-testid={`feed-card-${piece.id}`}>
      <header className="feed-card__head">
        <h3 className="feed-card__title" data-testid={`feed-title-${piece.id}`}>
          {piece.title}
        </h3>
        {piece.description ? (
          <p
            className="feed-card__description"
            data-testid={`feed-description-${piece.id}`}
          >
            {piece.description}
          </p>
        ) : (
          <p
            className="feed-card__description feed-card__description--placeholder"
            data-testid={`feed-description-${piece.id}`}
          >
            Description coming soon.
          </p>
        )}
      </header>
      <div className="feed-card__frame">
        {isMostLoved && (
          <span className="feed-card__badge" data-testid="badge-most-loved">
            ★ Most loved
          </span>
        )}
        <img
          className="feed-card__img"
          src={piece.src}
          alt={piece.alt}
          onClick={() => onOpen(piece)}
          data-testid={`feed-img-${piece.id}`}
        />
      </div>
      <div className="feed-card__actions">
        <button
          type="button"
          className={`feed-action ${liked ? "feed-action--liked" : ""}`}
          onClick={() => onToggleLike(piece)}
          aria-pressed={liked}
          aria-label="Like"
          data-testid={`like-${piece.id}`}
        >
          <Heart
            size={18}
            strokeWidth={1.5}
            fill={liked ? "currentColor" : "none"}
          />
          <span data-testid={`like-count-${piece.id}`}>
            {typeof count === "number" ? count : 0}
          </span>
        </button>
        <button
          type="button"
          className="feed-action"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid={`feelings-${piece.id}`}
        >
          <MessageCircleHeart size={18} strokeWidth={1.5} />
          <span>Feelings</span>
        </button>
      </div>
      {open && (
        <form
          className="feelings-form"
          onSubmit={startSend}
          data-testid={`feelings-form-${piece.id}`}
        >
          <textarea
            className="feelings-form__input"
            placeholder="Share your feelings — only the artist will read them."
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid={`feelings-input-${piece.id}`}
          />
          <div className="feelings-form__row">
            <span className="feelings-form__hint">
              {sentOnce ? "Received." : "Private — never shown publicly."}
            </span>
            <button
              type="submit"
              className="feelings-form__submit"
              disabled={!text.trim()}
              data-testid={`feelings-submit-${piece.id}`}
            >
              {sentOnce ? <Check size={14} /> : <Send size={14} />}
              <span>{sentOnce ? "Sent" : "Send"}</span>
            </button>
          </div>
        </form>
      )}
      {emailModalOpen && (
        <EmailPromptModal
          piece={piece}
          draft={text.trim()}
          onClose={() => setEmailModalOpen(false)}
          onSent={handleSent}
        />
      )}
    </article>
  );
};

// ============================================================================
// TalesFeed — orchestrates the live like-snapshot from the backend.
// ============================================================================
const TalesFeed = () => {
  const [counts, setCounts] = useState({});
  const [likedSet, setLikedSet] = useState(new Set());
  const [openIdx, setOpenIdx] = useState(null);
  const openPiece = openIdx !== null ? PIECES[openIdx] : null;

  useEffect(() => {
    let alive = true;
    http
      .get("/likes")
      .then((res) => {
        if (!alive) return;
        setCounts(res.data.counts || {});
        setLikedSet(new Set(res.data.liked || []));
      })
      .catch(() => {
        /* leave counts empty; UI shows 0 */
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggleLike = async (piece) => {
    // Optimistic update
    const previouslyLiked = likedSet.has(piece.id);
    setLikedSet((prev) => {
      const next = new Set(prev);
      if (previouslyLiked) next.delete(piece.id);
      else next.add(piece.id);
      return next;
    });
    setCounts((prev) => ({
      ...prev,
      [piece.id]: Math.max(0, (prev[piece.id] || 0) + (previouslyLiked ? -1 : 1)),
    }));
    try {
      const res = await http.post("/likes", { image_id: piece.id });
      // Sync to authoritative server state
      setCounts((prev) => ({ ...prev, [piece.id]: res.data.count }));
      setLikedSet((prev) => {
        const next = new Set(prev);
        if (res.data.liked) next.add(piece.id);
        else next.delete(piece.id);
        return next;
      });
    } catch {
      // Roll back optimistic state
      setLikedSet((prev) => {
        const next = new Set(prev);
        if (previouslyLiked) next.add(piece.id);
        else next.delete(piece.id);
        return next;
      });
      setCounts((prev) => ({
        ...prev,
        [piece.id]: Math.max(0, (prev[piece.id] || 0) + (previouslyLiked ? 1 : -1)),
      }));
      toast.error("Could not register your like.");
    }
  };

  // Derive the most-loved piece (only if at least one like exists; ties → first).
  const mostLovedId = (() => {
    let bestId = null;
    let bestCount = 0;
    for (const p of PIECES) {
      const c = counts[p.id] || 0;
      if (c > bestCount) {
        bestCount = c;
        bestId = p.id;
      }
    }
    return bestCount > 0 ? bestId : null;
  })();

  return (
    <>
      <SectionLayout>
        <div className="feed-wrap" data-testid="feed-wrap">
          <header className="feed-header">
            <h2 className="glass-panel__title" data-testid="section-title">
              Tales of Humanity and Technology
            </h2>
            <p className="feed-header__sub">A collection in motion.</p>
          </header>

          <div className="feed" data-testid="feed">
            {PIECES.map((p, i) => (
              <FeedCard
                key={p.id}
                piece={p}
                count={counts[p.id]}
                liked={likedSet.has(p.id)}
                isMostLoved={p.id === mostLovedId}
                onToggleLike={toggleLike}
                onOpen={() => setOpenIdx(i)}
              />
            ))}
          </div>

          <div className="glass-panel__nav feed-footer">
            <Link to="/" className="glass-panel__btn" data-testid="btn-home">
              Home
            </Link>
          </div>
        </div>
      </SectionLayout>
      {openPiece && (
        <Lightbox
          piece={openPiece}
          onClose={() => setOpenIdx(null)}
          onPrev={() =>
            setOpenIdx((i) => (i - 1 + PIECES.length) % PIECES.length)
          }
          onNext={() => setOpenIdx((i) => (i + 1) % PIECES.length)}
        />
      )}
    </>
  );
};

export default TalesFeed;
